import React, { useEffect, useState } from "react";
import axios from "axios";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { BanSignIcon, ReloadIcon } from "./Svgs";
import { ImportMetaEnv } from "../common/types";
import { ScanWordsResult } from "../common/dictionary";

const API_URL = (import.meta as ImportMetaEnv).env.VITE_API_URL as string;
console.log("API_URL", API_URL); // eslint-disable-line no-console

interface CrosswordEntryProps {
  clue: string;
  answer: string;
  highlightWord: string;
}

const CrosswordEntry = ({ clue, answer, highlightWord }: CrosswordEntryProps) => {
  const highlight = (text: string, _highlight: string) => {
    const parts = text.split(new RegExp(`(${_highlight})`, "gi"));
    return parts.map((part, i) =>
      part === _highlight ? (
        <span key={i} style={{ backgroundColor: "#9fc2ea" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <>
      {highlight(clue, highlightWord)}
      {" - "}
      {highlight(answer, highlightWord)}
    </>
  );
};

const StyledDiv = styled.div(
  () => css`
    font-size: 1.5em;
    line-height: 1.5;

    .two-cols {
      display: flex;
      justify-content: space-between;
      gap: 2rem;

      .left,
      .right {
        flex: 1;
      }
    }

    ul {
      padding: 0;
    }

    li {
      list-style-type: none; /* Remove default list item style */
      padding: 0;

      span + span {
        margin-left: 2rem;
      }

      span {
        cursor: pointer;

        &.false {
          text-decoration: line-through;
        }

        &:hover {
          background-color: #ccc;
        }

        .pipe {
          margin: 0 2px; /* Add extra space around the "|" character */
          color: #666;
          background-color: #666;
        }
      }

      &.skip {
        color: #ccc;

        .pipe {
          color: #ccc;
          background-color: #ccc;
        }
      }
    }

    button {
      border: none;
      background-color: transparent;
    }

    .reload-icon,
    .ban-sign-icon {
      width: 2rem;
      height: 2rem;

      path {
        fill: #333;
      }
    }

    .ban-sign-icon {
      margin-right: 1rem;
      vertical-align: -0.4rem;
    }
  `
);

export const ScanWordsPage: React.FC = () => {
  const [scanWords, setScanWords] = useState<ScanWordsResult>({} as ScanWordsResult);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [falseCompoundWords, setFalseCompoundWords] = useState<string[]>([]);
  const falseCompoundWordsSet = new Set(falseCompoundWords);

  const GET_ENDPOINT_URL = `${API_URL}/api/words`;
  const POST_ENDPOINT_URL = `${API_URL}/api/compound-words`;

  console.log("falseCompoundWords", falseCompoundWords); // eslint-disable-line no-console
  useEffect(() => {
    const fetchCompoundWords = async () => {
      try {
        const response = await axios.get<ScanWordsResult>(GET_ENDPOINT_URL);
        setScanWords(response.data);
      } catch {
        setError("Failed to fetch scan words");
      } finally {
        setLoading(false);
      }
    };

    fetchCompoundWords();
  }, []);

  const handlePost = async () => {
    try {
      const resp = await axios.post(POST_ENDPOINT_URL, {
        verifiedWords: Object.values(scanWords.compoundWords).map((d) => d.word),
        falseWords: falseCompoundWords,
      });
      // reset compoundWords and falseCompoundWords
      setScanWords(resp.data);
      setFalseCompoundWords([]);
    } catch (err) {
      console.error("Failed to send data to the server", err); // eslint-disable-line no-console
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <StyledDiv>
      <div className="two-cols">
        <div className="left">
          <ul>
            {Object.values(scanWords.compoundWords).map(({ word, variants }, i) => (
              <li key={i} className={falseCompoundWordsSet.has(word) ? "skip" : ""}>
                <button
                  onClick={() => {
                    if (falseCompoundWordsSet.has(word)) {
                      falseCompoundWordsSet.delete(word);
                    } else {
                      falseCompoundWordsSet.add(word);
                    }
                    setFalseCompoundWords([...falseCompoundWordsSet]);
                  }}
                >
                  <BanSignIcon />
                </button>
                {variants.map((d, j) => (
                  <span
                    key={j}
                    className={falseCompoundWordsSet.has(d) ? "false" : ""}
                    onClick={() => {
                      if (falseCompoundWordsSet.has(d)) {
                        falseCompoundWordsSet.delete(d);
                      } else {
                        falseCompoundWordsSet.add(d);
                      }
                      setFalseCompoundWords([...falseCompoundWordsSet]);
                    }}
                  >
                    {d.split("|").map((part, k) => (
                      <React.Fragment key={k}>
                        {k > 0 && <span className="pipe">|</span>}
                        {part}
                      </React.Fragment>
                    ))}
                  </span>
                ))}
              </li>
            ))}
          </ul>
          <button onClick={handlePost}>
            <ReloadIcon />
          </button>
        </div>
        <div className="right">
          <ul>
            {Object.entries(scanWords.unknownWords).map((d, i) => (
              <li key={i}>
                <span className="entry">
                  <CrosswordEntry clue={d[1].clue} answer={d[1].answer} highlightWord={d[0]} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </StyledDiv>
  );
};
